import { Box, Divider, Flex, Text, Tooltip, useTheme } from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import MetricColumn from './MetricColumn';
import {
  CustomTeamFilter,
  Loader,
  MetricTooltip,
  TeamNode,
  TeamsIcon,
  useGlobalState,
  useTeamComparisonStore,
} from '@devd-client/devd/components';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { useAuth } from '@devd-client/devd/auth';
import SelectMetricColumn from './SelectMetricColumn';
import { defaultMetrics } from '../../helpers/team-details.utils';
import { useFetchTeamComparisonTeamsList } from '../../api';

type Props = {
  isChecked: boolean;
  metricsList: MetricTooltip[];
  isLoading: boolean;
};

const TeamCompHeatMap = ({ isChecked, metricsList, isLoading }: Props) => {
  const [appState] = useAuth();
  const { colors } = useTheme();
  const { data: teamData } = useFetchTeamComparisonTeamsList();

  const teamsOptions: any = [];

  const collectAllSubTeams = (subTeams: TeamNode[]): TeamNode[] => {
    return subTeams.flatMap((subTeam) => [
      subTeam,
      ...(subTeam.subTeams?.length ? collectAllSubTeams(subTeam.subTeams) : []),
    ]);
  };

  teamData?.dto?.forEach((team: TeamNode) => {
    // top-level team option
    teamsOptions.push({
      value: team.name,
      label: team.name,
      id: team.name,
      memberCount: team.memberCount,
    });

    const allSubTeams = collectAllSubTeams(team.subTeams || []);

    // group option containing all descendant subTeams (any depth)
    if (allSubTeams.length > 0) {
      teamsOptions.push({
        value: team.name,
        label: team.name,
        memberCount: team.memberCount,
        options: allSubTeams.map((item: TeamNode) => ({
          id: team.name,
          value: item.name,
          label: item.name,
          memberCount: item.memberCount,
        })),
      });
    }
  });

  const { globalState } = useGlobalState();
  const {
    selectedTeams,
    setSelectedTeams,
    selectedMetrics,
    setSelectedMetrics,
  } = useTeamComparisonStore();

  useEffect(() => {
    if (!metricsList) return;
    if (globalState?.dto?.team_comparison_metrics) {
      const metrics = JSON.parse(globalState?.dto?.team_comparison_metrics);

      setSelectedMetrics(
        metrics.flatMap((metric: string) =>
          metricsList?.filter((item: MetricTooltip) =>
            item.name === 'CUSTOM' ? item.title === metric : item.id === metric
          )
        )
      );
    } else {
      setSelectedMetrics(
        metricsList?.filter((el: MetricTooltip) =>
          defaultMetrics.includes(el.name)
        )
      );
    }
  }, [metricsList]);

  useEffect(() => {
    if (!teamData) return;
    const allTeam = { value: 'all', label: 'All Teams' };
    const savedTeams =
      JSON.parse(globalState?.dto?.team_comparison_teams || '[]') || [];

    const teamOptionsFlattened = teamsOptions.flatMap((team: any) =>
      team.options ? team.options : team
    );

    const newSelectedTeams = savedTeams
      .filter((team: string) => team !== 'all')
      .map((team: string) =>
        teamOptionsFlattened.find((option: any) => option.value === team)
      )
      .filter((team: any) => !!team);

    if (isChecked) {
      if (!newSelectedTeams.some((team: any) => team?.value === 'all')) {
        setSelectedTeams([allTeam, ...newSelectedTeams]);
      }
    } else {
      setSelectedTeams(
        newSelectedTeams.filter((team: any) => team?.value !== 'all')
      );
    }
  }, [isChecked, teamData]);

  return (
    <Flex
      overflowX={'scroll'}
      overflowY={'auto'}
      border="1px solid #E0E0E0"
      height="480px"
      width="100%"
      borderRadius="8px"
      position="relative"
    >
      <Flex
        direction="column"
        position="sticky"
        left={0}
        bg="white"
        h="100%"
        minW="240px"
        zIndex={80}
      >
        <Flex
          bg="#FBFBFB"
          position="sticky"
          top={0}
          align="center"
          minH="52px"
          px={4}
          zIndex={50}
        >
          <CustomTeamFilter
            selectedTeams={selectedTeams}
            setSelectedTeams={setSelectedTeams}
            appState={appState}
            noDefault={false}
            isChecked={isChecked}
            teamsOptions={teamsOptions}
          >
            <Flex
              w="full"
              cursor="pointer"
              justify={'space-between'}
              align="center"
            >
              <Text
                fontSize="sm"
                fontFamily="heading"
                fontWeight="medium"
                color="text.secondary2"
                w="190px"
              >
                Teams
              </Text>
              <MdOutlineKeyboardArrowDown
                size={18}
                color={colors.text.secondary2}
              />
            </Flex>
          </CustomTeamFilter>
        </Flex>

        <Divider />

        {selectedTeams?.map((row: any) => (
          <Fragment key={row.label}>
            <Flex
              direction="column"
              align="flex-start"
              justify={'center'}
              minH="52px"
              pl="16px"
              bg="white"
            >
              <Text
                fontFamily={'heading'}
                fontSize="sm"
                fontWeight="normal"
                color="text.gray.300"
              >
                {row.label}
              </Text>
              {row.label !== 'All Teams' && (
                <Flex gap={1} align="center">
                  <Tooltip hasArrow label="Members">
                    <Box>
                      <TeamsIcon
                        height="12"
                        width="12"
                        color={colors.text.secondary3}
                      />
                    </Box>
                  </Tooltip>
                  <Text fontSize="xs" fontFamily="heading" color="text.primary">
                    {row.memberCount || 0}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Divider />
          </Fragment>
        ))}
      </Flex>

      {selectedMetrics?.map((metricData: any, idx: number) => (
        <MetricColumn
          data={metricData}
          key={idx}
          selectedTeams={selectedTeams}
          setSelectedMetrics={setSelectedMetrics}
          selectedMetrics={selectedMetrics}
          options={metricsList?.filter(
            (el: MetricTooltip) =>
              !selectedMetrics?.some((item: any) =>
                item.name === 'CUSTOM'
                  ? item.title === el.title
                  : item.name === el.name && item.title === el.title
              )
          )}
        />
      ))}
      <SelectMetricColumn
        data={selectedTeams || []}
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        options={metricsList?.filter(
          (el: MetricTooltip) =>
            !selectedMetrics?.some((item: any) =>
              item.name === 'CUSTOM'
                ? item.title === el.title
                : item.name === el.name && item.title === el.title
            )
        )}
      />
      {isLoading && <Loader />}
    </Flex>
  );
};

export default TeamCompHeatMap;
